import { IntegrationServices } from '@constants/integration';
import firebase from '@modules/firebase';

/**
 * Constructs a new type by making specified keys of the original type optional,
 * while keeping the remaining keys mandatory.
 *
 * @param T - The original type.
 * @param K - The keys to be made optional.
 * and the rest of the keys remaining mandatory.
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type PageSection = 'card' | 'qr-code' | 'email-signatures';
// Enums don't work in discrimated unions: https://github.com/microsoft/TypeScript/issues/33654
export type CardPageRouteInfo =
  | {
      pageMode: 'edit';
      tab: PageSection;
      params: { cardId: string; type: 'personal' | 'team' | undefined };
    }
  | { pageMode: 'view'; tab: PageSection; params: { cardId: string | undefined } }
  | {
      pageMode: 'create';
      tab: PageSection;
      params: { cardId: undefined; type: 'personal' | 'team'; from: string | undefined };
    }
  | { pageMode: 'invalid'; params: { cardId: undefined }; tab: undefined };

export type User = {
  userId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /**
   * The honorofic prefix to attach to the users name that is displayed.
   */
  honorificPrefix?: string;
  /**
   * The honorofic suffix to attach to the users name that is displayed.
   */
  honorificSuffix?: string;
  /**
   * The preferred first name
   */
  preferredName?: string;
  /**
   * The maiden name
   */
  maidenName?: string;
  /**
   * Pronouns that identify a persons gender
   */
  pronouns?: string;
  avatarUrl?: string;
  bbOrgId?: string;
  orgIds?: string[];
  creationTimestampMs?: number;
  //currentWorkspaceId?: string;
  currentCardId?: string;
  cardIds: string[];
  isAnonymous: boolean;
  providerId?: string;
  country?: string;
  companySize?: string;
  signInEmailAddress?: string;
  /**
   * The User's time zone, in IANA Time Zone database format.
   * @see https://datatracker.ietf.org/doc/html/rfc6557
   */
  timezone?: string;
  gclid?: string;
  fbclid?: string;
  uiEvents?: {
    appDownloaded?: boolean;
    showBusinessOnboarding?: boolean;
  };
  shouldNotReceiveRecipientEmails?: boolean;
  isIosDevice?: boolean;
};

/**
 * All the things we need to know about the user in order to determine their account type and
 * all their personal details
 */
export interface UserDetails {
  user: User | undefined;
  userTags: UserTags | undefined;
  userSubscriptions: StripeSubscription[];
}

export type AnonymousUser = Omit<User, 'email'>;

export interface NavigationState {
  collapsed?: boolean;
  minimized?: boolean;
  overridden: boolean;
}

export interface NavigationAction {
  type: 'MINIMIZE' | 'COLLAPSE' | 'APPEAR' | 'EXPAND' | 'TOGGLE_COLLAPSE';
  override?: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  members: User[];
  plan: string;
}

export interface Radius {
  tl: number;
  tr: number;
  br: number;
  bl: number;
}

export type Notification = {
  id?: string;
  type: 'warning' | 'error' | 'info';
  title: string;
  message?: string;
  size?: 'small' | 'regular';
  priority?: number;
} & (
  | {
      autoHideAfterTimeout?: true;
      timeout: number;
    }
  | {
      autoHideAfterTimeout: false;
      timeout?: never;
    }
);

export type CardUrlType =
  | 'company'
  | 'org'
  | 'calendly'
  | 'linkedin'
  | 'instagram'
  | 'twitter'
  | 'twitterX'
  | 'facebook'
  | 'youtube'
  | 'snapchat'
  | 'whatsapp'
  | 'paypal'
  | 'discord'
  | 'twitch'
  | 'telegram'
  | 'threads'
  | 'skype'
  | 'wechat'
  | 'tiktok'
  | 'github'
  | 'yelp'
  | 'venmo'
  | 'cashapp'
  | 'signal'
  | 'link'
  | 'referral'
  | 'promo'
  | 'company_url';

export interface MultiValuedField {
  value: string;
  id: string;
}

export interface CardUrl extends MultiValuedField {
  title: string;
  type: CardUrlType;
  /**
   * The value as a valid URL
   * @example https://twitter.com/username
   * @example https://youtube.com/c/hey
   */
  value: string;
  /**
   * The value as the user entered it
   * @example username
   * @example youtube.com/c/hey
   */
  userValue: string;
  isPreset: boolean;
  /**
   * Controls which field in the User Profile this is linked to. Linked fields will always reflect the
   * corresponding value in the User Profile.
   */
  linkedTo?: string;
}

export interface CardEmail extends MultiValuedField {
  label: string;
  isPreset: boolean;
  /**
   * Controls which field in the User Profile this is linked to. Linked fields will always reflect the
   * corresponding value in the User Profile.
   */
  linkedTo?: string;
}

export interface CardPhoneNumber extends MultiValuedField {
  label: string;
  /**
   * The phone number. This can either be in international format (i.e. the country calling code prepended to
   * the user input) OR it may be a localised phone number (users can opt into this behaviour).
   * Formatting is preserved.
   * @example +61 0422 123 234
   * @example 0422 123 234
   */
  value: string;
  extension?: string;
  countryCode?: string;
  isPreset: boolean;
  /**
   * Controls which field in the User Profile this is linked to. Linked fields will always reflect the
   * corresponding value in the User Profile.
   */
  linkedTo?: string;
}

export interface CardAddress extends MultiValuedField {
  label: string;
  type: 'org';
  isPreset: boolean;
  /**
   * Controls which field in the User Profile this is linked to. Linked fields will always reflect the
   * corresponding value in the User Profile.
   */
  linkedTo?: string;
}

export interface CardNameFields {
  /**
   * The given name to display.
   */
  firstName?: string;
  /**
   * The middle name to display.
   */
  middleName?: string;
  /**
   * The family name to display.
   */
  lastName?: string;
  /**
   * The honorofic prefix to attach to the users name that is displayed.
   */
  honorificPrefix?: string;
  /**
   * The honorofic suffix to attach to the users name that is displayed.
   */
  honorificSuffix?: string;
  /**
   * The preferred first name
   */
  preferredName?: string;
  /**
   * The maiden name
   */
  maidenName?: string;
  /**
   * Pronouns that identify a persons gender
   */
  pronouns?: string;
}

export interface CardUserDetailFields {
  /**
   * The job title to display.
   */
  jobTitle?: string;
  /**
   * The company name to display.
   */
  orgName?: string;
  /**
   * The department to display.
   */
  department?: string;
  /**
   * A headline to display. This can be pretty much anything.
   */
  headline?: string;
  /**
   * A list of accreditations to display.
   */
  accreditations?: string[];
}

export interface CardImage {
  originalImageUrl?: string;
  editedImageUrl?: string;
  editorMetadata?: {
    editorVersion?: string | undefined;
    backgroundColor?: string | undefined;
    imgPosition?: {
      x: number;
      y: number;
    } | null;
    zoom?: number | undefined;
    createdAt?: number | null | undefined;
    updatedAt?: number | null | undefined;
  };
}
export interface CardPicturesFields {
  /** Profile picture image data */
  profilePictureImg?: CardImage | null;
  /** Company logo image data */
  companyLogoImg?: CardImage | null;
  /** Cover photo image data */
  coverPhotoImg?: CardImage | null;
}

export enum CardLayout {
  /** profile pic in banner */
  Layout_1A = '1A',
  /** logo pic in banner */
  Layout_1B = '1B',
  /** cover in banner */
  Layout_1C = '1C',
  /** profile pic in banner, logo floating */
  Layout_2A = '2A',
  /** logo in banner, profile floating */
  Layout_2B = '2B',
  /** cover in banner, profile floating */
  Layout_2C = '2C',
  /** cover in banner, logo floating */
  Layout_2D = '2D',
  /** cover in banner, profile floating, logo floating */
  Layout_3A = '3A',
}

export interface CardLayoutFields {
  /** Controls the layout of the card header.
   * The enum syntax refers to the number of images and the variant.
   */
  layout?: CardLayout;
}
export interface MultiValuedComplexFields {
  /**
   * A list of emails to display.
   */
  emails?: CardEmail[];
  /**
   * A list of phone numbers to display.
   */
  phoneNumbers?: CardPhoneNumber[];
  /**
   * A list of URLs to display.
   */
  urls?: CardUrl[];
  /**
   * A list of addresses to display.
   */
  addresses?: CardAddress[];
}

/**
 * This will act as a go between until we phase out CardMetadata and just combine it into one unified card
 */
export interface CardOtherFields {
  /**
   * Controls which attributes are linked to the users User Profile specified by user_profile_id. If an attribute is linked
   * then any changes to it's corresponding attribute in the User Profile will be propogated to this card.
   * An attribute is not a field. A field is a something that is displayed, e.g. name. An attribute is what makes
   * up a field, e.g. first_name, last_name. Some fields only have a single attribute (e.g. job_title).
   */
  linkedAttributes: string[];
  /**
   * Determines which order any complex fields (e.g. address, url, ect) are displayed in. If a field is not referenced
   * in this array then it is just sorted in a default way.
   */
  fieldOrder: string[];
}

export type EmailSignature = {
  qrCode?: boolean;
  companyLogo?: boolean;
  profilePicture?: boolean;
  headline?: boolean;
  jobTitle?: boolean;
  accreditations?: boolean;
  orgName?: boolean;
  phoneNumbers?: EmailSignatureField;
  emails?: EmailSignatureField;
  urls?: EmailSignatureField;
  addresses?: EmailSignatureField;
  customBanner?: string;
  signatureId?: string;
};

export type EmailSignatureField = { [key: string]: { show: boolean; columns?: number } };

export interface CardFields
  extends CardNameFields,
    CardUserDetailFields,
    CardPicturesFields,
    MultiValuedComplexFields,
    CardLayoutFields,
    CardOtherFields {}

export type CardMultiFieldType = 'phone' | 'email' | 'address' | 'url';

export interface CardMetadata {
  /**
   * The ID of the card.
   */
  cardId: string;
  /**
   * The ID of the most recent edit of this card.
   * @deprecated
   */
  cardEditId: string;
  /**
   * The ID of the most recent update of this card.
   */
  cardUpdateId: string | undefined;
  /**
   * The ID of the user account that owns this card. This can be empty if the card has yet to be claimed.
   */
  userId?: string;
  /**
   * The organisation that this card belongs to. A card belongs to an organisation if it is created by the organisation admin.
   */
  orgId?: string;
  /**
   * When someone saves this card as a contact and downloads the VCARD to save the contact to their phones address book we will
   * append a referral link to download the Blinq app if this is true.
   */
  referralLinkEnabled?: boolean;
  /**
   * When someone saves this card as a contact and downloads the VCARD to save the contact to their phones address book we will
   * append a "Scanned from the Blinq app." message if this is true.
   */
  blinqMessageEnabled?: boolean;
  /**
   * When someone saves this card as a contact we will record where this occured if this flag is true.
   */
  whereWeMetEnabled?: boolean;
  /**
   * Controls whether a card is deleted or not. Think of this as a soft-delete. It is set to true when a card is deleted
   * by a user instead of actually deleting the card.
   */
  isInvisible: boolean;
  /**
   * Provides a way for users to stop a card from being viewed by others without actually deleting the card.
   * This is not currently in use.
   */
  isPaused: boolean;
  /**
   * Controls whether a card can be edited by anyone other than it's organisations admin. DON'T USE. This has been replaced
   * by all_field_types_locked.
   * @deprecated
   */
  cardIsEditable?: boolean;
  /**
   * Helps label cards so you can more easily tell your list of cards apart.
   */
  cardTitle: string;
  /**
   * The color of a card. Controls the color of the card fields, the card header (when no logo is present), and the
   * "Save Contact" button on the card share page.
   */
  color?: string;
  /**
   * The time a card was created at.
   */
  creationTimestampMs?: number;
  /**
   * The time a card was last updated at.
   */
  updatedTimestampMs?: number;
  /**
   * When a card is created by an organistion admin for a team member (or for themselves) they can assign a preset to that
   * card which will add a card_preset_id.
   */
  cardPresetId?: string | null;
  /**
   * When a card is created by an organistion admin for a team member they must choose a recipient email
   * to send the card to (instead of choosing which user to assign it to). Blinq has been designed this way so that
   * users can claim a card created by their business on their existing account. This way a user only needs to have
   * one account, instead of one account per organisation. In the future this design should be improved so that a user
   * account has several profiles (one for each org they belong to) and we assign a team card to a profile instead of a
   * recipient_email but that's future work.
   */
  recipientEmail?: string;
  /**
   * When a card is created by an organistion admin for a team member this card is "unactivated".
   * When the recipient (i.e. recipient_email) claims the card by clicking on the link that has been
   * sent to their email we record the timestamp this occured at.
   */
  bbActivationTimestampMs?: number;
  /**
   * When a user fills in the contact details form (displayed when clicking "Save Contact" on a card) a card
   * is created to represent them (using the details in the form, e.g. first_name, email, ect). When creating this
   * card we also store the ID of the card that referred them (i.e. the card that was scanned).
   */
  referrerCardId?: string;
  /**
   * When a user fills in the contact details form (displayed when clicking "Save Contact" on a card) a card
   * is created to represent them (using the details in the form, e.g. first_name, email, ect). When creating this
   * card we also store the ID of the user that referred them (i.e. the user that owns that card that was scanned).
   */
  referrerUserId?: string;
  /**
   * When a card is created by an organisation admin for a team member this will contain the
   * formatted name of the admin that created the card.
   */
  createdBy?: string;
  /**
   * Determines which card fields are displayed inside the cards email signature.
   */
  emailSignature?: EmailSignature;
  /**
   * Controls whether a logo will be displayed within a cards QR code.
   */
  logoIsInQr: boolean;
  /**
   * The URL of the logo to be displayed in the center of a cards QR code.
   */
  logoInQrUrl?: string;
  /**
   * Controls which existing fields can be edited on a card.
   */
  lockedFields: string[];
  /**
   * Controls which field types can be added to a card.
   */
  lockedFieldTypes: string[];
  /**
   * Controls whether a card owner can add any new fields to their card.
   */
  allFieldTypesLocked: boolean;
  /**
   * Controls which fields can be toggled on or off for a cards email signature.
   */
  lockedEmailSignatureFields: string[];
  /**
   * Controls whether a card owner can make any changes to their email signature.
   */
  allEmailSignatureFieldsLocked: boolean;
  /**
   * Controls whether a card owner can make any changes to their cards color.
   */
  colorLocked: boolean;
  /**
   * Controls whether a card owner can make any changes to their cards QR code.
   */
  qrCodeLocked: boolean;
  /**
   * Controls whether a card owner can make any changes to their cards header section (layout or images).
   */
  layoutImagesLocked: boolean | undefined;
  /**
   * Allows us to keep track of if and when an activation email has been sent to the card owner
   */
  activationEmailSentAt?: firebase.firestore.Timestamp;
  /**
   * The user profile that this card is linked to and therefore can sync with. In the future this won't be
   * necessary because we will allow every card to sync on a per field level, and the user profile will own
   * these cards (instead of the user owning them).
   */
  userProfileId?: string;
  /**
   * The ID of the Profile (New API)
   */
  profileId?: string;
}

export interface CardPresetMetadata {
  cardPresetId: string;
  cardPresetName: string;
  presetEditable: boolean;
  orgId: string;
  orgName?: string;
  referralLinkEnabled: boolean;
  blinqMessageEnabled: boolean;
  whereWeMetEnabled: boolean;
  isInvisible: boolean;
  cardIsEditable: boolean;
  color: string;
  creationTimestampMs?: number;
  updatedTimestampMs?: number;
  layoutImagesLocked: boolean | undefined;
  logoIsInQr: boolean;
  logoInQrUrl?: string;
  emailSignature?: CardPresetEmailSignature;
  lockedFields: string[];
  lockedFieldTypes: string[];
  allFieldTypesLocked: boolean;
  lockedEmailSignatureFields: string[];
  allEmailSignatureFieldsLocked: boolean;
  colorLocked: boolean;
  qrCodeLocked: boolean;
  /**
   * If true or not defined then any changes to the Live Template will be immediately propogated
   * to the cards it's connected to.
   */
  syncUpdates: boolean;
}

export interface Card extends CardFields, CardMetadata {}

export type CardPresetUrl = Omit<CardUrl, 'isPreset' | 'linkedTo'>;
export type CardPresetEmail = Omit<CardEmail, 'isPreset' | 'linkedTo'>;
export type CardPresetAddress = Omit<CardAddress, 'isPreset' | 'linkedTo'>;
export type CardPresetPhoneNumber = Omit<CardPhoneNumber, 'isPreset' | 'linkedTo'>;
export type CardPresetEmailSignature = Omit<EmailSignature, 'signatureId'>;

export interface CardPresetLinks {
  emails?: CardPresetEmail[];
  phoneNumbers?: CardPresetPhoneNumber[];
  urls?: CardPresetUrl[];
  addresses?: CardPresetAddress[];
}

/**
 * This will act as a go between until we phase out CardMetadata and just combine it into one unified card
 */
export interface CardPresetOtherFields {
  /**
   * Determines which order any complex fields (e.g. address, url, ect) are displayed in. If a field is not referenced
   * in this array then it is just sorted in a default way.
   */
  fieldOrder: string[];
}

export interface CardPresetFields
  extends CardUserDetailFields,
    CardLayoutFields,
    CardPicturesFields,
    CardPresetLinks,
    CardPresetOtherFields {}

export interface CardPreset extends CardPresetFields, CardPresetMetadata {}

export type FieldTypes =
  | 'name'
  | 'job_title'
  | 'org_name'
  | 'department'
  | 'accreditations'
  | 'headline'
  | 'phone'
  | 'email'
  | 'address'
  | 'url';

export interface ContactTag {
  id: string;
  value: string;
}

export interface Contact {
  card: Card;
  cardEditId?: string;
  cardId: string;
  contactId: string;
  creationTimestampMs?: number;
  isInvisible: boolean;
  recipientCardEditId?: string;
  recipientCardId?: string;
  recipientEmail?: string;
  location?: string;
  userId?: string;
  tags?: ContactTag[] | undefined | null;
  source?: string | null;
}

export interface BusinessContactRecipient {
  autoImportToBB?: boolean;
  bbSharedTimestampMs?: number;
  cardEditId?: string;
  contactId: string;
  creationTimestampMs?: number;
  recipientCardEditId?: string;
  recipientCardId?: string;
  location?: string;
}

export interface BusinessContact {
  autoImportToBB: boolean;
  bbContactId: string;
  bbSharedTimestampMs?: number;
  card: Card;
  cardId: string;
  creationTimestampMs?: number;
  isInvisible: boolean;
  orgId: string;
  recipientCardEditIds?: string[]; // Shared card version on which the form was filled out
  recipientCardIds?: string[]; // Shared card on which the form was filled out
  recipients: Record<string, BusinessContactRecipient>; // They key is the user ID
  referrerCardId?: string;
  exportedTo?: ExportedTo[];
}

export interface ExportedTo {
  service: string;
  id: string;
  /**
   * Id of the integration invovled in this export
   */
  integrationId?: string;
  /**
   * After direct export, a contact id should be return, which will
   * be stored as externalId
   */
  externalId?: string;

  lastExportTimestamp: firebase.firestore.Timestamp;
}

export interface CardSave {
  cardId: string;
  cardEditId: string;
  orgId?: string;
  date: string;
  savedAt?: firebase.firestore.Timestamp;
  location: string;
  /**
   * @deprecated
   */
  timestampMs?: number;
}

/**
 * This is a combination of a stripe product and stripe price for ease of use.
 * Note: we don't display inactive products so there is no need for the active paramater
 */
export interface StripeProduct {
  name: string;
  stripeMetadataMaxCards: string;
  priceId: string; // From price subdocument
  currency: string;
  unitAmount: number; // From price subdocument
  /**
   * A brief description of the price.
   */
  description: string | null;
  /**
   * One of `one_time` or `recurring` depending on whether the price is for a one-time purchase or a recurring (subscription) purchase.
   */
  type: 'one_time' | 'recurring';
  /**
   * The frequency at which a subscription is billed. One of `day`, `week`, `month` or `year`. From price subdocument.
   */
  interval: 'day' | 'month' | 'week' | 'year';
  /**
   * The number of intervals (specified in the `interval` attribute) between subscription billings. For example, `interval=month` and `interval_count=3` bills every 3 months.
   */
  intervalCount: number | null;
  /**
   * Default number of trial days when subscribing a customer to this price using [`trial_from_plan=true`](https://stripe.com/docs/api#create_subscription-trial_from_plan).
   */
  trialPeriodDays: number | null;
  /**
   * For telling apart the business plan (metered) from the premium plan (licensed)
   */
  usageType: 'metered' | 'licensed';
}

export interface StripeSubscription {
  /**
   * Set of key-value pairs that you can attach to an object.
   * This can be useful for storing additional information about the object in a structured format.
   */
  metadata: Record<string, string>;
  stripeLink: string;
  role: string | null;
  quantity: number;
  /**
   * Firestore reference to the product doc for this Subscription.
   */
  product: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>;
  /**
   * Firestore reference to the price for this Subscription.
   */
  price: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>;
  /**
   * Array of price references. If you prvoide multiple recurring prices to the checkout session via the `line_items` parameter,
   * this array will hold the references for all recurring prices for this subscription. `price === prices[0]`.
   */
  prices: Array<firebase.firestore.DocumentReference<firebase.firestore.DocumentData>>;
  /**
   * The status of the subscription object
   */
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
  /**
   * If true the subscription has been canceled by the user and will be deleted at the end of the billing period.
   */
  cancelAtPeriodEnd: boolean;
  /**
   * Time at which the object was created.
   */
  created: firebase.firestore.Timestamp;
  /**
   * Start of the current period that the subscription has been invoiced for.
   */
  currentPeriodStart: firebase.firestore.Timestamp;
  /**
   * End of the current period that the subscription has been invoiced for. At the end of this period, a new invoice will be created.
   */
  currentPeriodEnd: firebase.firestore.Timestamp;
  /**
   * If the subscription has ended, the timestamp of the date the subscription ended.
   */
  endedAt: firebase.firestore.Timestamp | null;
  /**
   * A date in the future at which the subscription will automatically get canceled.
   */
  cancelAt: firebase.firestore.Timestamp | null;
  /**
   * If the subscription has been canceled, the date of that cancellation. If the subscription was canceled with `cancel_at_period_end`, `canceled_at` will still reflect the date of the initial cancellation request, not the end of the subscription period when the subscription is automatically moved to a canceled state.
   */
  canceledAt: firebase.firestore.Timestamp | null;
  /**
   * If the subscription has a trial, the beginning of that trial.
   */
  trialStart: firebase.firestore.Timestamp | null;
  /**
   * If the subscription has a trial, the end of that trial.
   */
  trialEnd: firebase.firestore.Timestamp | null;
}

export interface ContactNote {
  contactId: string;
  contactNoteId: string;
  createTimestamp: number;
  createTimezone?: string;
  isInvisible: boolean;
  note: string;
  updateTimestamp: number;
  updateTimezone?: string;
  userId: string;
}

export interface FilterAttribute {
  name: string;
}

export interface Filter {
  id: string;
  attribute: string;
  operator: 'is' | 'is-not'; // Could be more options
  attributeValue: string;
}

export type FilterCombinator = 'and' | 'or';

export interface UserProvisioningAttributeFilter {
  /**
   * Used purely by the web app for handling modifications and removals
   */
  id: string;
  /**
   * The name of the SCIM User Resource attribute.
   */
  attribute: 'job_title' | 'department';
  /**
   * The operator determines what check we run against the attribute specified by attribute_name.
   */
  operator: 'is' | 'is-not';
  /**
   * The value of the attribute that we expect/don't expect.
   */
  attributeValue: string;
}

export interface UserProvisioningPresetFilter {
  /**
   * Used purely by the web app for handling modifications and removals
   */
  id: string;
  /**
   * The ID corresponding to the card preset that should be applied if combination of the attribute filters
   * (using the combinator) match the SCIM User Resource.
   */
  cardPresetId: string;
  /**
   * Controls how the attribute filters are combined
   */
  combinator?: FilterCombinator;
  /**
   * A list of filters that should be applied to a SCIM User Resource in order to check whether the
   * card preset specified by the card_preset_id should be applied.
   */
  attributeFilters: UserProvisioningAttributeFilter[];
}

export type BBOrg = {
  canEmailAnyone?: boolean;
  creationTimestampMs?: number;
  name: string;
  orgId: string;
  avatarUrl?: string;
  companySize?: string;
  validEmailEndings?: string[];
  /**
   * When a user is provisioned from another service (e.g. Azure AD) we want to allow for their
   * card to be automatically assigned to a preset. However we can't assume that the org will only
   * have 1 preset (they might have 1 for each department) OR that they will want a preset assigned
   * to these cards. This field allows an admin to control which preset is applied to a provisioned
   * card if no filters match.
   */
  defaultUserProvisioningPresetId?: string;
  /**
   * An organization admin will have the ability to assign presets to provisioned cards depending on
   * a set of filters, e.g. Assign Preset A to this newly provisioned card if user is in Deperatment A.
   * These filters take precedence over the default if there is a match.
   */
  userProvisioningPresetFilters?: UserProvisioningPresetFilter[];
  /**
   * This controls whether an activation email is sent out automatically whenever a new card is provisioned
   * using an external user directory (e.g. Microsoft Azure AD).
   */
  autoSendProvisionedUserActivationEmail?: boolean;
  /**
   * This controls whether new contacts are synced automatically if there is any CRM that has been setup
   */
  autoExportContacts?: boolean;
  isDemoAccount?: boolean;
  adminUserId?: string;
  maxCards?: number;
  overrideMaxCards?: number;
  subscriptionId?: string;
  subscriptions?: StripeSubscription[];
  /**
   * Keep track of the cards that have been paid for in the current billing cycle.
   * This is handy for telling when to turn on and off proration for a subscription in Stripe.
   */
  paidForCardCount?: number;
};

export interface UserTags {
  canAddCustomColor: boolean;
  canAddLogoInQr: boolean;
  canCreateMultipleCards: boolean;
  canDisplayPhoneNumber: boolean;
  canRemoveBlinqTag: boolean;
  canRemoveCardReferralLink: boolean;
  isPremiumSubscriber: boolean;
  isWebPremiumSubscriber: boolean;
  isBbEmployee: boolean;
  isBlinqEmployee: boolean;
  canBookOnboarding: boolean;
  canExportContacts?: boolean;
}

export type AccessoryRow = {
  accessoryId: string;
  name: string;
  secondaryField: string | undefined;
  company: string | undefined;
  email: string | undefined;
  dateMet: number | undefined;
  interactedWith: string | undefined;
};

export const AccessoryTypes = [
  'card_plastic_essential',
  'card_plastic_light',
  'card_plastic_dark',
  'card_plastic_custom',
  'external',
] as const;
export type AccessoryType = (typeof AccessoryTypes)[number];

export interface CardAccessoryField {
  value: string;
  id: string;
}

export interface CardAccessoryLogoField extends CardAccessoryField {
  /**
   * The maximum width the logo can occupy in mm
   */
  maxWidth?: number;
  /**
   * The maximum height the logo can occupy in mm
   */
  maxHeight?: number;
}

export interface CardAccessoryFields {
  firstName?: CardAccessoryField;
  lastName?: CardAccessoryField;
  secondaryField?: CardAccessoryField;
  logo?: CardAccessoryLogoField;
}

export type Accessory = {
  id: string;
  orgId?: string;
  userId?: string;
  createdAt?: firebase.firestore.Timestamp;
  updatedAt?: firebase.firestore.Timestamp;
  purchasedAt?: firebase.firestore.Timestamp;
  deletedAt?: firebase.firestore.Timestamp;
  notifiedAt?: firebase.firestore.Timestamp;
  cardId?: string;
  status?:
    | 'FINALIZING_DESIGN'
    | 'MANUFACTURING'
    | 'READY_FOR_PICKUP'
    | 'IN_TRANSIT'
    | 'DELIVERED'
    | 'FAILURE';
  title?: string;
  imageFront?: string | null;
  imageBack?: string | null;
  createdBy?: User;
  orderId?: number;
} & (
  | {
      type: 'card_plastic_essential';
      fields?: never;
      showBlinqLogo?: never;
    }
  | {
      type: 'card_plastic_light' | 'card_plastic_dark';
      fields: CardAccessoryFields;
      showBlinqLogo: boolean;
    }
  | {
      type: 'card_plastic_custom';
      fields?: never;
      showBlinqLogo?: never;
    }
  | {
      type: 'external';
      fields?: never;
      showBlinqLogo?: never;
    }
);

export type InformationContainerAlignment = 'topLeft' | 'bottomLeft' | 'topRight' | 'bottomRight';

export interface PersonalAccessToken {
  /**
   * The personal access tokens unique identifier.
   */
  id: string;
  /**
   * The ID of the organization associated with this access token. The access token will only be able to affect resources
   * within this organization.
   */
  orgId: string;
  /**
   * The ID of the user that generated the access token.
   */
  userId: string;
  /**
   * The time at which this access token was created.
   */
  createdAt: firebase.firestore.Timestamp;
  /**
   * The last time at which this access token was used.
   */
  lastUsedAt?: firebase.firestore.Timestamp;
  /**
   * A name that allows the user to differentiate one token from the next.
   */
  name: string;
  /**
   * Contains any extra data we may wish to attach to the access token, for example what it should be used for (i.e. user
   * provisioning).
   */
  metadata: Record<string, string>;
}

export type Integration = {
  /**
   * ID of the user who sets up this integration
   */
  userId: string;
  orgId?: string;
  /**
   * Controls what this integration can do with the external service
   */
  actions: Array<'add' | 'read' | 'update' | 'delete'>;
  /**
   * The unique id of integration
   */
  integrationId: string;
  /**
   * Which part of business logic, i.e. card, contact,
   */
  businessDomain: string;
  /**
   * The time at which this integration was deleted.
   */
  deletedAt: firebase.firestore.Timestamp | null;
  /**
   * The time at which this integration was created.
   */
  createdAt: firebase.firestore.Timestamp;
  /**
   * The time at which this integration was created.
   */
  updatedAt: firebase.firestore.Timestamp;
} & (
  | {
      settings: DirectlyConnectedCRMIntegrationSettings;
      service: IntegrationServices.HUBSPOT;
      data: Record<string, never>;
    }
  | {
      settings: ZapierIntegrationSettings;
      service: IntegrationServices.ZAPIER;
      data: ZapierIntegrationData;
    }
  | {
      settings: DirectlyConnectedCRMIntegrationSettings;
      service: IntegrationServices.SALESFORCE;
      data: Record<string, never>;
    }
);
export interface ZapierIntegrationData {
  /**
   * subscription webhook sent by the service provider.
   * If a user create more than one Zap with same Blinq trigger/configuration,
   * all target urls are inserted into this integration. For example, a user create two
   * Zaps, each trigger in the zap has same add contact configuration. Then these two
   * zaps use same intergraion as trigger is identical except target urls.
   */
  targetUrls: string[];
}

export interface ZapierIntegrationSettings {
  /**
   * Controls whether new business contact will be sent to subscribed CRM.
   */
  autoExportContacts: boolean;
}

export interface DirectlyConnectedCRMIntegrationSettings {
  /**
   * controls whether business contact will be synced if there is an update.
   */
  autoSyncContacts: boolean;
  /**
   * Controls whether new business contact will be sent to subscribed CRM.
   */
  autoExportContacts: boolean;
  exportToLeads?: boolean;
}

export interface UserProfileEmail {
  /**
   * The unique identifier for an email.
   */
  id: string;
  /**
   * Email address for the user.
   */
  value: string;
  /**
   * The primary attribute value 'true' MUST appear no more than once.
   */
  primary?: boolean;
  /**
   * Provides a classification meaningful to the (human) user.
   */
  type?: string;
  /**
   * A human-readable name, primarily used for display purposes.
   */
  display?: string;
}

export interface UserProfilePhoneNumber {
  /**
   * The unique identifier for a phone number.
   */
  id: string;
  /**
   * Phone number of the User.
   */
  value: string;
  /**
   * The primary attribute value 'true' MUST appear no more than once.
   */
  primary?: boolean;
  /**
   * A human-readable name, primarily used for display purposes.
   */
  display?: string;
  /**
   * A label indicating the attribute's function.
   * @example "work", "home", "mobile", "fax", "pager", "other"
   */
  type?: string;
}

export interface UserProfileAddress {
  /**
   * The unique identifier for an address.
   */
  id: string;
  /**
   * The full mailing address, formatted for display or use with a mailing label.
   * This attribute MAY contain newlines.
   */
  formatted?: string | null;
  /**
   * The full street address component, which may include house number,
   * street name, P.O. box, and multi-line extended street address information.
   * This attribute MAY contain newlines.
   */
  streetAddress?: string;
  /**
   * The city or locality component.
   */
  locality?: string;
  /**
   * The state or region component.
   */
  region?: string;
  /**
   * The zip code or postal code component.
   */
  postalCode?: string;
  /**
   * The country name component.
   */
  country?: string;
  /**
   * A label indicating the attribute's function.
   * @example "work", "home"
   */
  type?: 'work' | 'home' | 'other';
  /**
   * The primary attribute value 'true' MUST appear no more than once.
   */
  primary?: boolean;
}

/**
 * Every user that is part of an Organization and has been provisioned using an external
 * directory will have a User Profile that is kept in sync with the external directory. In
 * the future User Profiles will be used to tie a User Account to a Workspace, so if a User is
 * part of 5 Workspaces they will have 5 User Profiles. This will give us a clear seperation around
 * what assets a User has for a given workspace, and it will also make adding new Users much simpler.
 * If an admin invites a user to their workspace it will create a User Profile for them and tie their card
 * to that profile. Then when the user accepts the invite it will tie the User Profile to their account
 * or if they didn't have an account it will also create an account before tying it.
 */
export interface UserProfile {
  /**
   * The user profiles unique identifier.
   */
  id: string;
  /**
   * The ID of the user that is linked to this User Profile.
   */
  userId?: string;
  /**
   * Unique identifier for the user, typically used by the user to
   * directly authenticate to the provisioning client. Many systems use
   * an email address as a user identifier, but we don't want to rely on that since
   * email addresses often change.
   */
  userName?: string;
  /**
   * A String that is an identifier for the resource as defined by the
   * provisioning client (e.g. Azure AD). This allows for the client to filter
   * on an ID that they control (and we store for them) so they don't have to
   * store a local mapping between their unique identifier and ours.
   */
  externalId?: string;
  /**
   * The time at which this user profile was created.
   */
  createdAt: firebase.firestore.Timestamp;
  /**
   * The time at which this user profile was created.
   */
  updatedAt: firebase.firestore.Timestamp;
  /**
   * The ID corresponding to the organization this user is a part of and this user profile is associated with.
   */
  orgId: string;
  /**
   * The users given name for the assigned organization.
   */
  givenName?: string;
  /**
   * The users middle name for the assigned organization.
   */
  middleName?: string;
  /**
   * The users family name for the assigned organization.
   */
  familyName?: string;
  /**
   * The honorific prefix(es) of the User, or title in
   * most Western languages (e.g., "Ms." given the full name
   * "Ms. Barbara Jane Jensen, III").
   */
  honorificPrefix?: string;
  /**
   * The honorific suffix(es) of the User, or suffix
   * in most Western languages (e.g., "III" given the full name
   * "Ms. Barbara Jane Jensen, III").
   */
  honorificSuffix?: string;
  /**
   * The users full name, formatted for display. This should describe the same
   * name as first_name, last_name, ect., essentially indicating how those other attributes
   * should be combined.
   */
  formattedName?: string;
  /**
   * The job title to display.
   */
  jobTitle?: string;
  /**
   * Used to identify the relationship between the organization and the
   * user. Typical values used might be "Contractor", "Employee",
   * "Intern", "Temp", "External", and "Unknown", but any value may be
   * used.
   */
  userType?: string;
  /**
   * The name of the user, suitable for display to end-users. This enables clients to
   * display the user information in the same way that we do.
   */
  displayName?: string;
  /**
   * Indicates the user's preferred written or spoken languages and is
   * generally used for selecting a localized user interface. The
   * value indicates the set of natural languages that are preferred. This
   * is something that provisioning clients (e.g. Azure AD) will request that we store.
   */
  preferredLanguage?: string;
  /**
   * Used to indicate the User's default location for purposes of
   * localizing such items as currency, date time format, or numerical
   * representations. This is not currently used by us but can be provided
   * by the provisioning client and may be useful in the future.
   */
  locale?: string;
  /**
   * The User's time zone, in IANA Time Zone database format.
   * @see https://datatracker.ietf.org/doc/html/rfc6557
   */
  timezone?: string;
  /**
   * Indicates whether the user is able to access the assigned organization
   */
  active?: boolean;
  /**
   * Email addresses for the user.
   */
  emails?: UserProfileEmail[];
  /**
   * Phone numbers for the User.
   */
  phoneNumbers?: UserProfilePhoneNumber[];
  /**
   * The physical mailing addresseses for this User.
   */
  addresses?: UserProfileAddress[];
  /**
   * The profile picture URL for this User.
   */
  profilePictureUrl?: string;
  /**
   * A string identifier, typically numeric or alphanumeric, assigned
   * to a person, typically based on order of hire or association with
   * an organization.
   */
  employeeNumber?: string;
  /**
   * Identifies the name of a cost center.
   */
  costCenter?: string;
  /**
   * Identifies the name of an organization.
   */
  organization?: string;
  /**
   * Identifies the name of a division.
   */
  division?: string;
  /**
   * Identifies the name of a department.
   */
  department?: string;
  /**
   * A fully qualified URL pointing to a page representing the User's online profile
   */
  profileUrl?: string;
}

export type CardImageEditorCropShape = 'rectangle' | 'ellipse';
